/* istanbul ignore file */
import { FC, useState } from "react";
import * as _ from "lodash";
import { Redirect, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { CompassOutlined, CtraLayout } from "@ctra/components";
import { isPending, useDidMount } from "@ctra/utils";
import { GAContext } from "@ctra/analytics";
import { Breadcrumbs } from "@ctra/api";

import {
  Enterprise,
  EnterpriseAppState,
  GamePlanEntity,
  GamePlanFormValues,
  GamePlans,
  TodoItemEntity
} from "@ctra/api";

import { useLocalization } from "@base";
import { useConversion, Wizard, WizardDomain, WizardIntent, GACategories } from "@game-plans";
import { CompiledRoutes, Routes } from "@routes";

const { WidgetWrapper } = CtraLayout;

type EditGamePlanProps = {
  gamePlanID: GamePlanEntity["id"];
};

/**
 * Wrapper component for creating a game plan
 * @constructor
 */
export const EditGamePlan: FC<EditGamePlanProps> = ({ gamePlanID }) => {
  const routes = CompiledRoutes.app.gamePlans.gamePlan.edit;

  const [mayRedirect, setMayRedirect] = useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isMounting] = useDidMount();
  const [fromFormValues, toFormValues] = useConversion();
  const { unitSystem } = useLocalization();

  /**
   * Get a game plan with tasks included
   */
  const gamePlan = useSelector<
    EnterpriseAppState,
    Omit<GamePlanEntity, "taskList"> & {
      taskList: TodoItemEntity[];
    }
  >((state) => Enterprise.entities.getGamePlanWithTasks(state, { gamePlanID }));

  const spinning = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      isPending(state, GamePlans.types.UPDATE_GAME_PLAN, {
        primaryValue: gamePlanID
      }) ||
      isPending(state, GamePlans.types.UPDATE_TODO_LIST, {
        primaryValue: gamePlanID
      })
  );

  const updated = mayRedirect && !isMounting && !spinning;

  /**
   * Convert the game plan to form values
   */
  const initialValues: GamePlanFormValues = toFormValues({ ...gamePlan, globalUnitSystem: unitSystem });

  return (
    <WidgetWrapper
      icon={<CompassOutlined />}
      title={t<string>(Content.gamePlan.wizard.title.update, { title: gamePlan.title })}
    >
      <GAContext.Provider value={{ category: GACategories.gamePlanEditor }}>
        <Breadcrumbs.components.Breadcrumb title={t<string>(Content.gamePlan.wizard.title.updateHeader)} />
        {/* Redirect from edit index to the first step */}
        <Route exact path={routes.index({ gamePlanID })}>
          {({ location: { pathname, search } }) => (
            <Redirect to={{ pathname: routes.general({ gamePlanID }), search }} />
          )}
        </Route>
        {/* Redirect after successfully updating a game plan */}
        {updated && <Redirect to={Routes.app.gamePlans.index} />}
        <Wizard
          spinning={spinning}
          paths={_.mapValues(routes, (makeRoute) => makeRoute({ gamePlanID }))}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) => {
            dispatch(GamePlans.actions.updateGamePlan.start(fromFormValues(values)));
            setMayRedirect(true);
            setSubmitting(false);
          }}
          domain={WizardDomain.Global}
          intent={WizardIntent.Edit}
        />
      </GAContext.Provider>
    </WidgetWrapper>
  );
};
